import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticlesByCategory } from '../services/contentService';
import { Article } from '../types';
import { ArticleCard } from '../components/ArticleCard';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, BookOpen } from 'lucide-react';

export const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [articles, setArticles] = useState<Article[]>([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (category) {
      setLoading(true);
      getArticlesByCategory(category).then(data => {
        setArticles(data);
        setLoading(false);
        setVisibleCount(5);
      });
    }
  }, [category]);

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const categoryTitle = category ? (category.toLowerCase() === 'opinion' ? 'Opinions' : capitalize(category)) : 'News';

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400"></div></div>;

  if (articles.length === 0) {
    return (
      <div className="max-w-xl mx-auto text-center py-24 px-4 uppercase">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-300">
          <BookOpen size={40} />
        </div>
        <h2 className="text-2xl font-sans font-black text-brand-900 mb-2">No stories found in {categoryTitle} Beat</h2>
        <p className="text-sm text-gray-500 mb-6 font-serif italic">Our reporters are currently working on deep dives for this beat. Check back shortly for the latest updates.</p>
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-brand-400 hover:text-brand-900 uppercase tracking-widest group">
          Return to Front Page <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead title={`${categoryTitle} News`} />
      <div className="animate-fade-in pb-12">
        <header className="mb-12 border-b border-black pb-4 flex items-baseline justify-between">
          <h1 className="text-2xl font-black font-sans text-brand-900 uppercase tracking-tight">{categoryTitle}</h1>
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{articles.length} Stories</span>
        </header>
        <section className="mb-16"><ArticleCard article={articles[0]} featured /></section>
        <section className="max-w-3xl mx-auto space-y-12">
          {articles.slice(1, visibleCount).map(article => <ArticleCard key={article.id} article={article} />)}
          {articles.length > visibleCount && (
            <div className="text-center pt-8"><button onClick={() => setVisibleCount(v => v + 5)} className="px-8 py-3 bg-white border border-gray-300 font-bold uppercase tracking-widest text-xs hover:bg-brand-400 hover:text-white transition-colors">Load More</button></div>
          )}
        </section>
      </div>
    </>
  );
};
